import { useMemo } from 'react';

import { useCoworkerGroupsQuery } from 'data/queries/useCoworkerGroupsQuery.hook';
import { useCoworkerVisibility } from 'floorplan/hooks/use-coworker-visibility.hook';
import { CoworkerGroupResponse } from 'store/api-client/coworker-types';
import { useActiveCoworkerGroup } from './use-active-coworker-group.hook';

export interface UseCoworkerGroupsValues {
  /** Groups are available at all — `useCoworkerVisibility().effectiveTeams`. */
  hasGroups: boolean;
  groups: CoworkerGroupResponse[];
  /**
   * Focus mode (§C3): the group the url names, resolved against the requester's groups and the teams
   * gate. Null when there is none, teams are off, or the uuid is stale (deleted group, foreign link).
   */
  activeGroup: CoworkerGroupResponse | null;
  /** `activeGroup`'s members as a set for the canvas; null without an active group. */
  focusGroupMemberUuids: ReadonlySet<string> | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The requester's coworker groups and which one the map is focused on — one answer for the sheet's
 * chips, the map's focus mode, the legend's first entry and the Coworkers button's active state, so
 * they cannot disagree. The groups are fetched only while teams are on (the BE answers 403
 * otherwise), and TanStack Query shares the one request between every consumer.
 */
export const useCoworkerGroups = (): UseCoworkerGroupsValues => {
  const { effectiveTeams } = useCoworkerVisibility();
  const { activeGroupUuid } = useActiveCoworkerGroup();
  const { groups, isLoading, isError } = useCoworkerGroupsQuery({ enabled: effectiveTeams });

  const activeGroup = useMemo(
    () => (effectiveTeams && activeGroupUuid ? groups.find(group => group.uuid === activeGroupUuid) ?? null : null),
    [effectiveTeams, activeGroupUuid, groups],
  );
  const focusGroupMemberUuids = useMemo(
    () => (activeGroup ? new Set(activeGroup.memberUserUuids) : null),
    [activeGroup],
  );

  return useMemo(
    () => ({ hasGroups: effectiveTeams, groups, activeGroup, focusGroupMemberUuids, isLoading, isError }),
    [effectiveTeams, groups, activeGroup, focusGroupMemberUuids, isLoading, isError],
  );
};
